import { useState } from "react";
import {
  Scene,
  Vector2,
  Raycaster,
  Mesh,
  BoxGeometry,
  MeshLambertMaterial,
  PerspectiveCamera,
  Color,
  MeshBasicMaterial,
  TextureLoader,
  GridHelper,
  PlaneGeometry,
  WebGLRenderer,
  AmbientLight,
  DirectionalLight,
  Object3D
} from "three";

import squareOutline from "./textures/square-outline-textured.png";

export const useBuilds = () => {
  const [isLoadingHome, setIsLoadingHome] = useState<boolean>(false);

  let camera: PerspectiveCamera, scene: Scene, renderer: WebGLRenderer;
  let plane: Mesh;
  let pointer: Vector2,
    raycaster: Raycaster,
    isShiftDown = false;

  let rollOverMesh: Mesh, rollOverMaterial;
  let cubeGeo: BoxGeometry, cubeMaterial: MeshLambertMaterial;

  const objects: Object3D[] = [];

  const onWindowResize = () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();

    renderer.setSize(window.innerWidth, window.innerHeight);

    render();
  };

  const onPointerMove = (event: MouseEvent) => {
    pointer.set(
      (event.clientX / window.innerWidth) * 2 - 1,
      -(event.clientY / window.innerHeight) * 2 + 1
    );

    raycaster.setFromCamera(pointer, camera);

    const intersects = raycaster.intersectObjects(objects, false);

    if (intersects.length > 0) {
      const intersect = intersects[0];
      /* eslint-disable  @typescript-eslint/no-non-null-assertion */
      rollOverMesh.position.copy(intersect.point).add(intersect.face!.normal);
      rollOverMesh.position
        .divideScalar(50)
        .floor()
        .multiplyScalar(50)
        .addScalar(25);

      render();
    }
  };

  const onPointerDown = (event: MouseEvent) => {
    pointer.set(
      (event.clientX / window.innerWidth) * 2 - 1,
      -(event.clientY / window.innerHeight) * 2 + 1
    );

    raycaster.setFromCamera(pointer, camera);

    const intersects = raycaster.intersectObjects(objects, false);

    if (intersects.length > 0) {
      const intersect = intersects[0];

      // delete cube

      if (isShiftDown) {
        if (intersect.object !== plane) {
          scene.remove(intersect.object);

          objects.splice(objects.indexOf(intersect.object), 1);
        }

        // create cube
      } else {
        const voxel = new Mesh(cubeGeo, cubeMaterial);
        voxel.position.copy(intersect.point).add(intersect.face!.normal);
        voxel.position
          .divideScalar(50)
          .floor()
          .multiplyScalar(50)
          .addScalar(25);
        scene.add(voxel);

        objects.push(voxel);
      }

      render();
    }
  };

  const onDocumentKeyDown = (event: KeyboardEvent) => {
    switch (event.keyCode) {
      case 16:
        isShiftDown = true;
        break;
    }
  };

  const onDocumentKeyUp = (event: KeyboardEvent) => {
    switch (event.keyCode) {
      case 16:
        isShiftDown = false;
        break;
    }
  };

  const init = () => {
    camera = new PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      1,
      10000
    );
    camera.position.set(450, 800, 900);
    camera.lookAt(0, 0, 0);

    scene = new Scene();
    scene.background = new Color(0xf0f0f0);

    // roll-over helpers

    const rollOverGeo = new BoxGeometry(50, 50, 50);
    rollOverMaterial = new MeshBasicMaterial({
      color: 0xff0000,
      opacity: 0.5,
      transparent: true
    });
    rollOverMesh = new Mesh(rollOverGeo, rollOverMaterial);
    scene.add(rollOverMesh);

    // cubes
    const texture = new TextureLoader().load(squareOutline);
    cubeGeo = new BoxGeometry(50, 50, 50);
    cubeMaterial = new MeshLambertMaterial({
      color: 0xfeb74c,
      map: texture
    });

    // grid

    const gridHelper = new GridHelper(800, 16);
    scene.add(gridHelper);

    //

    raycaster = new Raycaster();
    pointer = new Vector2();

    const geometry = new PlaneGeometry(800, 800);
    geometry.rotateX(-Math.PI / 2);

    plane = new Mesh(geometry, new MeshBasicMaterial({ visible: false }));
    scene.add(plane);

    objects.push(plane);

    // lights

    const ambientLight = new AmbientLight(0x606060);
    scene.add(ambientLight);

    const directionalLight = new DirectionalLight(0xffffff);
    directionalLight.position.set(1, 0.75, 0.5).normalize();
    scene.add(directionalLight);

    renderer = new WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.domElement.id = "canvasBuilds";
    renderer.setSize(window.innerWidth, window.innerHeight);
    document.body.appendChild(renderer.domElement);

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onDocumentKeyDown);
    document.addEventListener("keyup", onDocumentKeyUp);

    //

    window.addEventListener("resize", onWindowResize);
  };

  const render = () => {
    renderer.render(scene, camera);
  };
  const deleteBuilds = () => {
    document.getElementById("canvasBuilds")!.remove();
  };
  return { isLoadingHome, setIsLoadingHome, init, render, deleteBuilds };
};
